import { FC, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';
import { AccountData, Permission, TeamData } from 'common/graphqlTypes';
import { useScalyrLogger } from 'common/ScalyrLogger';
import { clearGraphQLErrorMessage } from 'common/util/string';
import { getTeamDisplayName } from 'common/util/account';

import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import Link from 'components/common/Link/Link';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { availableNonactiveTeamsState } from 'components/account/account.state';

import { LinkAccountForm, LinkAccountModal } from './components';
import { GET_LINK_ACCOUNT_QUERY, SET_LINK_MUTATION } from './queries';
import { FormState, AccountData as LinkAccountPageAccountData } from './types';
import './styles.scss';

/**
 * This page allows to link or un-link user account to team account.
 * @returns {Component}
 */

const LinkAccountPage: FC = () => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const originalUrl = useRecoilValue(searchParamState(K.URL_PARAM.ORIGINAL_URL));
  const availableNonactiveTeams = useRecoilValue(availableNonactiveTeamsState);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isAccountLinked, setIsAccountLinked] = useState(false);
  const { sendLogsAndStopLogging } = useScalyrLogger();

  /* Cleanup on page unmount */
  useEffect(
    () => () => {
      setPageAlertConfig(K.NO_PAGE_ALERT);
    },
    [],
  );

  const handleIsAccountLinked = (account: AccountData): void => {
    setIsAccountLinked(account.email.toLowerCase() !== account.activeTeamV2.emailAddress.toLowerCase());
  };

  const {
    loading: linkAccountLoading,
    data: linkAccountData,
    error: linkAccountError,
  } = useQuery(GET_LINK_ACCOUNT_QUERY, {
    onCompleted: ({ account }) => {
      handleIsAccountLinked(account);
    },
  });

  const handleShowModal = (message: string): void => {
    setModalMessage(message);
    setShowModal(true);
  };

  const activeTeamPermissionMapper: Record<Permission, string> = {
    [Permission.owner]: 'owner',
    [Permission.fullAccess]: 'full access',
    [Permission.logRead]: 'logs read',
    [Permission.logWrite]: 'logs write',
    [Permission.limited]: 'limited',
    [Permission.configRead]: 'configuration read',
    [Permission.configWrite]: 'configuration write',
    [Permission.userConfigWrite]: 'user access',
  } as Record<Permission, string>;

  const [setLink] = useMutation(SET_LINK_MUTATION, {
    onCompleted: ({
      switchTeams,
    }: {
      switchTeams: { activeTeamV2: TeamData; activeTeamPermission: Permission; email: string };
    }) => {
      const isLinked = switchTeams.email.toLowerCase() !== switchTeams.activeTeamV2.emailAddress.toLowerCase();
      const message = isLinked
        ? `Linked to account [${getTeamDisplayName(switchTeams.activeTeamV2)}] with permission ${
            activeTeamPermissionMapper[switchTeams.activeTeamPermission]
          }`
        : 'Your account is no longer linked to any other account';
      handleShowModal(message);
    },
    onError: (error: ApolloError): void => {
      setPageAlertConfig({
        message: clearGraphQLErrorMessage(error.message),
        variant: PageAlertVariant.ERROR,
        dismissible: true,
      });
    },
  });

  if (linkAccountLoading) {
    return <LoadingPlaceholder />;
  }

  if (linkAccountError) {
    return <ErrorPlaceholder error={linkAccountError} />;
  }

  const handleCloseModal = (): void => {
    window.location.href = originalUrl || K.ROUTE.ROOT;
  };

  const accountData: LinkAccountPageAccountData = {
    linkedToAccount: getTeamDisplayName(linkAccountData.account.activeTeamV2),
    linkedAccountPermissionName: linkAccountData ? linkAccountData.account.activeTeamPermission : '',
    quickSwitchEmails: linkAccountData ? linkAccountData.account.teamsV2 : [],
    isSSOAuthenticated: linkAccountData ? linkAccountData.account.isSSOAuthenticated : false,
  };

  const handleSubmit = (formState: FormState): void => {
    sendLogsAndStopLogging();
    setLink({
      variables: {
        targetAccountEmail: formState.email,
        password: accountData.isSSOAuthenticated ? null : formState.password,
      },
    });
  };

  const renderLinkAccountPermission = accountData.linkedAccountPermissionName ? (
    <p className="LinkAccount__LinkAccountPermission">
      Permission:
      <span>{accountData.linkedAccountPermissionName}</span>
    </p>
  ) : (
    <p>
      <span className="LinkAccount__Warning">You do not have permission to access this account.</span>
      Ask the account owner to add you to the account, or link to a different account.
    </p>
  );

  const renderQuickSwitchEmails = (
    <ul className="LinkAccount__EmailsList">
      {availableNonactiveTeams.map((team) => (
        <li key={team.id} className="LinkAccount__EmailSwitch">
          <span
            data-test-id={ELEMENT.linkAccountPageEmailSwitchItem}
            className="LinkAccount__EmailSwitch--TeamLink"
            onClick={(): void => handleSubmit({ email: team.emailAddress })}
          >
            {team.displayName}
          </span>
          {team.displayName.toLowerCase() !== team.emailAddress.toLowerCase() && <span>{`${team.emailAddress}`}</span>}
        </li>
      ))}
    </ul>
  );

  const isPasswordAuthenticated = !accountData.isSSOAuthenticated;

  return (
    <>
      <Page docTitle="Link Account" testId={ELEMENT.linkAccountPage}>
        <PageHeader title="Link Account" helpLink={HELP_ROUTE.HELP_TEAMS} />
        <p>
          Use this page to access your employer&apos;s (or other organization&apos;s) {K.PRODUCT_NAME} data. Enter the
          e-mail address of the master account below
          {isPasswordAuthenticated ? ', along with your password (not the password of the master account), ' : ' '}
          and click Set Link.
        </p>
        <p>
          To un-link your account, erase the &quot;Account to Use&quot; field
          {isPasswordAuthenticated ? ', enter your password, ' : ' '}
          and click Set Link.
        </p>
        <p>
          If you&apos;ve built dashboards, parsing rules, or other configuration in your personal account, this
          configuration will not be migrated to the master account. Data (e.g. logs) in your personal account will also
          not be migrated. The configuration and data will still be available in your account if you unlink from the
          master account.
        </p>
        <p>
          If you&apos;re switching between multiple accounts, accounts to which you&apos;ve previously been linked will
          be listed here as &quot;quick-switch&quot; links.
        </p>
        <h4 className="SectionHeader LinkAccount__Title">Current Status</h4>
        <div className="LinkAccount__Wrapper">
          {isAccountLinked ? (
            <>
              <p className="LinkAccount__LinkAccount" data-test-id={ELEMENT.linkAccountPageStatus}>
                Linked to account:
                <span>{accountData.linkedToAccount}</span>
              </p>
              {renderLinkAccountPermission}
            </>
          ) : (
            <p data-test-id={ELEMENT.linkAccountPageStatus}>Not linked to any account.</p>
          )}
          {availableNonactiveTeams.length > 0 && (
            <>
              <p>Quick-switch:</p>
              {renderQuickSwitchEmails}
            </>
          )}
        </div>
        <h4 className="SectionHeader">Update Account</h4>
        <LinkAccountForm onSubmit={handleSubmit} isSSOAuthenticated={accountData.isSSOAuthenticated} />
        <p className="LinkAccount__Wrapper">
          (Before you can link to an account, the account owner must grant permission. For further information, see the
          <Link openInNewTab className="link LinkAccount__Link" href={HELP_ROUTE.HELP_USERS}>
            Manage Users
          </Link>
          and
          <Link openInNewTab className="link LinkAccount__Link" href={HELP_ROUTE.HELP_TEAMS}>
            Manage Teams
          </Link>
          documentation.)
        </p>
      </Page>
      <LinkAccountModal showModal={showModal} handleShowModal={handleCloseModal} modalMessage={modalMessage} />
    </>
  );
};

export default LinkAccountPage;
